import React from "react";
import styled from "styled-components";
import { Badge, Card } from "react-bootstrap";
import { Clock } from "react-feather";
import { format } from "date-fns";
import { ActivityLogLevel, IActivityLog } from "../../../interfaces";

const LogWrapper = styled(Card)`
  margin-bottom: 8px;
  border-left: 3px solid;
  border-left-color: ${(props) => props.color};
`;

const LogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
`;

const LogTime = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
`;

const LogMessage = styled(Card.Body)`
  padding: 8px 12px;
  white-space: pre-wrap;
`;

const TimestampIcon = styled(Clock)`
  margin-right: 5px;
  width: 14px;
  height: 14px;
`;

const getLogLevelColor = (level: ActivityLogLevel): string => {
  switch (level) {
    case ActivityLogLevel.DEBUG:
      return "#6c757d"; // gray
    case ActivityLogLevel.INFO:
      return "#0d6efd"; // blue
    case ActivityLogLevel.WARN:
      return "#ffc107"; // yellow
    case ActivityLogLevel.ERROR:
      return "#dc3545"; // red
    default:
      return "#6c757d";
  }
};

const getLogLevelVariant = (level: ActivityLogLevel): string => {
  switch (level) {
    case ActivityLogLevel.DEBUG:
      return "secondary";
    case ActivityLogLevel.INFO:
      return "primary";
    case ActivityLogLevel.WARN:
      return "warning";
    case ActivityLogLevel.ERROR:
      return "danger";
    default:
      return "secondary";
  }
};

export const LogItem: React.FC<IActivityLog> = ({
  level,
  message,
  timestamp,
  metadata,
}) => {
  const color = getLogLevelColor(level);
  const variant = getLogLevelVariant(level);

  return (
    <LogWrapper color={color}>
      <LogHeader>
        <Badge bg={variant}>{level.toUpperCase()}</Badge>
        <LogTime>
          <TimestampIcon />
          {format(new Date(timestamp), "MMM d, yyyy h:mm:ss a")}
        </LogTime>
      </LogHeader>
      <LogMessage>
        {message}
        {metadata && Object.keys(metadata).length > 0 && (
          <details>
            <summary>Metadata</summary>
            <pre>{JSON.stringify(metadata, null, 2)}</pre>
          </details>
        )}
      </LogMessage>
    </LogWrapper>
  );
};
